import Script from "next/script";
import { createContext, type ReactNode, useCallback, useContext, useMemo, useState } from "react";
import StorageItem from "utils/StorageItem";

import { useBrowserPrinter } from "./browser-printer";
import { useQzPrinter } from "./qz-tray";

type PrinterConfig = "QZ" | "BROWSER";

type PrinterActions = {
  print: (value: string | string[]) => Promise<void>;
  printer: PrinterConfig;
  changePrinter: (value: PrinterConfig) => void;
  error: boolean;
  clearError: () => void;
};

const printerItem = new StorageItem("__web_pos_printer__");
const PrinterContext = createContext<PrinterActions | undefined>(undefined);

PrinterContext.displayName = "PrinterContext";

const PrinterProvider = ({ children }: { children: ReactNode }) => {
  const [printer, setPrinter] = useState(() => (printerItem.get() || "BROWSER") as PrinterConfig);

  const changePrinter = useCallback(
    (newPrinter?: PrinterConfig) => {
      if (!newPrinter || newPrinter === printer) return;
      printerItem.save(newPrinter);
      setPrinter(newPrinter);
    },
    [printer],
  );

  const [error, setError] = useState(false);
  const qzTrayPrint = useQzPrinter();
  const qzPrint = useCallback(
    async (data: string | string[]) => {
      await qzTrayPrint(data).catch((err) => {
        setError(true);
        throw err;
      });
    },
    [qzTrayPrint],
  );

  const browserPrint = useBrowserPrinter();

  const value = useMemo(
    () => ({
      print: printer === "QZ" ? qzPrint : browserPrint,
      printer,
      changePrinter,
      error,
      clearError: () => setError(false),
    }),
    [printer, qzPrint, browserPrint, changePrinter, error],
  );

  return (
    <PrinterContext.Provider value={value}>
      {printer === "BROWSER" ? (
        <>
          <Script src="/js/BrowserPrint-3.0.216.min.js" />
          <Script src="/js/BrowserPrint-Zebra-1.0.216.min.js" />
        </>
      ) : null}
      {children}
    </PrinterContext.Provider>
  );
};

function usePrinter() {
  const context = useContext(PrinterContext);

  if (context === undefined) {
    throw new Error(`usePrinter must be used inside PrinterProvider`);
  }

  return context;
}

export { PrinterProvider, usePrinter };
